import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { RESOURCE_FILE_ACCEPT, RESOURCE_LINK_KINDS } from '../../constants/resources';

export function FieldLabel({ children, required }) {
  return (
    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
      {children}
      {required && <span className="text-brand-cyan ml-1">*</span>}
    </label>
  );
}

export function TextInput({ className = '', ...props }) {
  return (
    <input
      className={`w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-all ${className}`}
      {...props}
    />
  );
}

export function TextArea({ className = '', ...props }) {
  return (
    <textarea
      className={`w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-all resize-y min-h-[120px] ${className}`}
      {...props}
    />
  );
}

export default function ResourceContentFields({
  contentType,
  onContentTypeChange,
  file,
  onFileChange,
  externalUrl,
  onExternalUrlChange,
  linkKind,
  onLinkKindChange,
  versionNote,
  onVersionNoteChange,
  versionLabel,
  onVersionLabelChange,
  showVersionFields = false,
  showVersionLabel = false,
  submitting = false,
}) {
  return (
    <div className="space-y-6">
      <section className="flex gap-2 p-1 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-full w-fit">
        {[
          { id: 'file', label: 'Archivo' },
          { id: 'link', label: 'Enlace externo' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            disabled={submitting}
            onClick={() => onContentTypeChange(tab.id)}
            className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
              contentType === tab.id
                ? 'gfi-gradient-bg text-brand-navy shadow-md'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </section>

      {contentType === 'file' ? (
        <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <FieldLabel required>Archivo (PDF, DOCX o Markdown)</FieldLabel>
          <input
            type="file"
            accept={RESOURCE_FILE_ACCEPT}
            disabled={submitting}
            onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-[var(--text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:gfi-gradient-bg file:text-brand-navy"
          />
          {file && (
            <p className="text-xs text-[var(--text-secondary)] flex items-center gap-2">
              <SafeIcon name="FileText" className="w-4 h-4 text-brand-green" />
              {file.name}
            </p>
          )}
        </section>
      ) : (
        <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <div>
            <FieldLabel required>URL del enlace</FieldLabel>
            <TextInput
              type="url"
              value={externalUrl}
              onChange={(e) => onExternalUrlChange(e.target.value)}
              disabled={submitting}
              placeholder="https://..."
              required
            />
          </div>
          <div>
            <FieldLabel required>Tipo de enlace</FieldLabel>
            <select
              value={linkKind}
              onChange={(e) => onLinkKindChange(e.target.value)}
              disabled={submitting}
              className="w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-green"
            >
              {RESOURCE_LINK_KINDS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </section>
      )}

      {showVersionFields && (
        <section className="space-y-4">
          {showVersionLabel && (
            <div>
              <FieldLabel>Etiqueta de versión (opcional)</FieldLabel>
              <TextInput
                value={versionLabel}
                onChange={(e) => onVersionLabelChange(e.target.value)}
                disabled={submitting}
                placeholder="Ej. 2.1 — si está vacío se asigna automáticamente"
              />
            </div>
          )}
          <div>
            <FieldLabel>Nota de esta versión</FieldLabel>
            <TextInput
              value={versionNote}
              onChange={(e) => onVersionNoteChange(e.target.value)}
              disabled={submitting}
              placeholder="Ej. Ajuste de tasas actuales"
            />
          </div>
        </section>
      )}
    </div>
  );
}
